import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import { merge, Subject, debounceTime, filter, takeUntil } from 'rxjs';
import {
  ClientsService,
  SelectedClient,
} from 'src/app/services/clients/clients.service';
import {
  ClientCreditHistoryItem,
  ClientCreditTableItem,
  CreditsService,
} from 'src/app/services/credits/credits.service';
import {
  MatCard,
  MatCardHeader,
  MatCardTitle,
  MatCardContent,
  MatCardFooter,
} from '@angular/material/card';
import { DateFilterComponent } from './date-filter/date-filter.component';
import { CreditSpendingTableComponent } from './credit-spending-table/credit-spending-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { CreditHistoryItemComponent } from './credit-history-item/credit-history-item.component';

@Component({
  selector: 'app-credit-history',
  templateUrl: './credit-history.component.html',
  styleUrls: ['./credit-history.component.scss'],
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    DateFilterComponent,
    CreditSpendingTableComponent,
    MatProgressSpinner,
    PayeeIconComponent,
    CreditHistoryItemComponent,
    MatCardFooter,
    MatPaginator,
    TranslateModule,
  ],
})
export class CreditHistoryComponent implements OnInit, OnDestroy {
  private readonly clientsService = inject(ClientsService);
  private readonly creditsService = inject(CreditsService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  private readonly destroy = new Subject<void>();

  readonly dateFrom = new FormControl<Date | null>(null);
  readonly dateTo = new FormControl<Date | null>(null);

  get dateFromFilter(): string | undefined {
    if (!this.dateFrom.value) {
      return undefined;
    }
    return format(this.dateFrom.value, 'yyyy-MM-dd') || undefined;
  }

  get dateToFilter(): string | undefined {
    if (!this.dateTo.value) {
      return undefined;
    }
    return format(this.dateTo.value, 'yyyy-MM-dd') || undefined;
  }

  readonly tableLoading = signal(false);
  readonly historyLoading = signal(false);

  creditTable: ClientCreditTableItem[] = [];
  creditHistory: ClientCreditHistoryItem[] = [];
  pagedCreditHistory: ClientCreditHistoryItem[] = [];

  pageIndex = 0;
  pageSize = 5;

  ngOnInit(): void {
    this.dateFrom.patchValue(startOfMonth(subMonths(new Date(), 1)));
    this.dateTo.patchValue(endOfMonth(subMonths(new Date(), 1)));

    merge(
      this.clientsService.selectedClientSubject.pipe(
        filter((client): client is SelectedClient => !!client),
      ),
      this.translate.onLangChange,
      this.dateFrom.valueChanges,
      this.dateTo.valueChanges,
    )
      .pipe(takeUntil(this.destroy), debounceTime(100))
      .subscribe(async () => await this.refresh());
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  pageChange(event: PageEvent): void {
    this.pagedCreditHistory = this.creditHistory.slice(
      event.pageIndex * event.pageSize,
      (event.pageIndex + 1) * event.pageSize,
    );
  }

  async refresh(): Promise<void> {
    await Promise.all([this.setTable(), this.setHistory()]);
  }

  async setTable(): Promise<void> {
    if (
      this.tableLoading() ||
      !this.clientsService.selectedClientId ||
      !this.dateFromFilter ||
      !this.dateToFilter
    ) {
      return;
    }

    try {
      this.tableLoading.set(true);
      this.creditTable = await this.creditsService.getCreditTable({
        from: this.dateFromFilter,
        to: this.dateToFilter,
      });
    } catch (error) {
      console.error('Error while loading table', error);
      this.snackbar.open(
        this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
        this.translate.instant('OK'),
        {
          duration: 5000,
        },
      );
    } finally {
      this.tableLoading.set(false);
    }
  }

  async setHistory(): Promise<void> {
    if (
      this.historyLoading() ||
      !this.clientsService.selectedClientId ||
      !this.dateFromFilter ||
      !this.dateToFilter
    ) {
      return;
    }

    try {
      this.historyLoading.set(true);
      this.creditHistory = [];
      this.pageIndex = 0;

      this.creditHistory = await this.creditsService.getCreditHistory({
        from: this.dateFromFilter,
        to: this.dateToFilter,
      });
    } catch (error) {
      console.error('Error while loading history', error);
      this.snackbar.open(
        this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
        this.translate.instant('OK'),
        {
          duration: 5000,
        },
      );
    } finally {
      this.historyLoading.set(false);
      this.pageChange({
        length: this.creditHistory.length,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
      });
    }
  }
}
